import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { Button } from '@mui/material';
import styles from './DateTimeRangePicker.module.css';
import { TimeZoneConstants } from 'src/constants';
import { TimeView } from '@mui/x-date-pickers';

/**
 * Props for the DateTimeRangePicker component.
 */
interface DateTimeRangePickerProps {
  /**
   * Timezone string (e.g., 'America/Los_Angeles') to use for date/time display and calculations.
   * Should follow IANA timezone database format.
   */
  timezone: TimeZoneConstants.AllowedTimezone;

  /**
   * Recommended start date-time to initialize the picker with.
   * Will be used as default value if provided and no manual selection has been made.
   */
  recommendedStart?: DateTime;

  /**
   * Recommended end date-time to initialize the picker with.
   * Will be used as default value if provided and no manual selection has been made.
   */
  recommendedEnd?: DateTime;

  /**
   * Callback function that fires when either the start or end date changes.
   * Receives both the updated start and end DateTime objects (or null if cleared).
   * This callback fires with valid dates after successful selection/validation.
   */
  onRangeChange?: (start: DateTime | null, end: DateTime | null) => void;

  /**
   * Optional ID values to apply to the start and end date picker input fields.
   * Useful for testing or accessibility purposes.
   */
  pickerIds?: { start: string; end: string };
}

/**
 * Helper function to get the current DateTime in the specified timezone.
 */
const getCurrentDateTimeInTimezone = (timezone: TimeZoneConstants.AllowedTimezone): DateTime => {
  return DateTime.now().setZone(timezone);
};

/**
 * A date-time range picker component that allows users to select start and end dates with time.
 *
 * Features:
 * - Vertical layout with start date on top, end date below
 * - Built-in validation to ensure start date comes before end date
 * - Timezone support for proper date/time handling
 * - Visual feedback for validation errors
 * - Clear button to reset both dates
 * - Today button to quickly set dates to current/next day
 * - Visual divider separating date pickers and action buttons
 *
 * @example
 * ```tsx
 * <DateTimeRangePicker
 * timezone="America/Los_Angeles"
 * recommendedStart={DateTime.now()}
 * recommendedEnd={DateTime.now().plus({ days: 1 })}
 * onRangeChange={(start, end) => console.log(start, end)}
 * />
 * ```
 */
export function DateTimeRangePicker({
  timezone,
  recommendedStart,
  recommendedEnd,
  onRangeChange,
  pickerIds,
}: DateTimeRangePickerProps) {
  // Use a ref to track if the user has manually interacted with the pickers
  const hasUserInteracted = useRef(false);

  // State for current 'now' to ensure shouldDisableTime can react to minute changes
  const [currentNow, setCurrentNow] = useState(() => getCurrentDateTimeInTimezone(timezone));

  // Periodic update for currentNow to keep time disabling accurate
  useEffect(() => {
    // Skip interval in test environment to prevent infinite loop with fake timers
    if (process.env.NODE_ENV === 'test') {
      setCurrentNow(getCurrentDateTimeInTimezone(timezone));
      return;
    }

    const intervalId = setInterval(() => {
      setCurrentNow(getCurrentDateTimeInTimezone(timezone));
    }, 60000); // Update every minute
    return () => clearInterval(intervalId);
  }, [timezone]);

  // Memoize the minimum date time allowed (1 hour from currentNow)
  const minDateTime = useMemo(() => {
    return currentNow.startOf('minute').plus({ hours: 1 });
  }, [timezone, currentNow]); // Automatically updates when currentNow changes

  // State for dates and validation errors
  const [startDate, setStartDate] = useState<DateTime | null>(null);
  const [endDate, setEndDate] = useState<DateTime | null>(null);
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);

  /**
   * Validates a date against the minimum allowed date and other constraints.
   * @param date The date to validate.
   * @param isEndDate Whether this is an end date (requiring start date comparison).
   * @param startDate The current start date for comparison when validating end date.
   * @returns An error message or null if valid.
   */
  const getDateError = useCallback(
    (
      date: DateTime | null,
      isEndDate = false,
      startDate: DateTime | null = null,
    ): string | null => {
      if (!date) return null; // No error if date is null (cleared)
      if (!date.isValid) return 'Invalid date format';

      // End date specific validation
      if (isEndDate && startDate && date <= startDate) {
        return 'End date must be after start date';
      }

      // Common validation for both start and end dates
      if (date < minDateTime) {
        return `${isEndDate ? 'End' : 'Start'} date must be at least one hour in the future`;
      }

      return null;
    },
    [minDateTime],
  );

  /**
   * Centralized function to set and validate dates.
   * This is called on onChange and onAccept for both pickers.
   */
  const updateAndValidateDates = useCallback(
    (
      newStartDate: DateTime | null,
      newEndDate: DateTime | null,
      isUserInteraction: boolean = true,
    ) => {
      if (isUserInteraction) {
        hasUserInteracted.current = true; // Mark that user has interacted
      }

      // Validate and set start date
      const sError = getDateError(newStartDate, false);
      setStartDate(newStartDate);
      setStartDateError(sError);

      // Validate and set end date (using the potentially newly set startDate for validation)
      const eError = getDateError(newEndDate, true, newStartDate);
      setEndDate(newEndDate);
      setEndDateError(eError);
    },
    [getDateError],
  );

  // Effect to apply recommended dates on initial render or when props change
  useEffect(() => {
    // Only apply recommended dates if the user hasn't interacted and props are provided
    if (!hasUserInteracted.current && (recommendedStart || recommendedEnd)) {
      let effectiveStart = recommendedStart && recommendedStart.isValid ? recommendedStart : null;
      let effectiveEnd = recommendedEnd && recommendedEnd.isValid ? recommendedEnd : null;

      // Check for invalid recommendedEnd specifically
      if (recommendedEnd && !recommendedEnd.isValid) {
        console.warn(`Invalid recommendedEnd date: ${recommendedEnd.invalidReason}`);
      }

      // Check for invalid recommendedStart specifically
      if (recommendedStart && !recommendedStart.isValid) {
        console.warn(`Invalid recommendedStart date: ${recommendedStart.invalidReason}`);
      }

      if (effectiveStart && effectiveStart < minDateTime) {
        console.warn(
          'recommendedStart date must be at least one hour in the future. Adjusting to minDateTime.',
        );
        effectiveStart = minDateTime;
      }

      // Set end time to minimum of recommended end time or start time + 1 day
      if (effectiveStart) {
        const startPlusOneDay = effectiveStart.plus({ days: 1 });
        if (effectiveEnd) {
          effectiveEnd = effectiveEnd < startPlusOneDay ? effectiveEnd : startPlusOneDay;
        } else {
          effectiveEnd = startPlusOneDay;
        }
      } else if (effectiveEnd && effectiveEnd < minDateTime) {
        console.warn(
          'recommendedEnd date must be at least one hour in the future. Adjusting to minDateTime.',
        );
        effectiveEnd = minDateTime;
      }

      // Apply and validate the effective dates
      updateAndValidateDates(effectiveStart, effectiveEnd, false);
    }
  }, [recommendedStart, recommendedEnd, updateAndValidateDates, minDateTime]);

  // Effect to call onRangeChange when internal state changes (after validation)
  useEffect(() => {
    // Only call onRangeChange if startDate or endDate have non-null values
    // and there are no validation errors for the respective dates.
    // This prevents calling with transient invalid states.
    if (onRangeChange) {
      const finalStart = startDateError ? null : startDate;
      const finalEnd = endDateError ? null : endDate;
      // Only trigger if at least one value is not null or both were previously not null and are now null
      // (to handle clearing)
      if (
        finalStart !== null ||
        finalEnd !== null ||
        (startDate !== null && endDate !== null && finalStart === null && finalEnd === null)
      ) {
        onRangeChange(finalStart, finalEnd);
      }
    }
  }, [startDate, endDate, startDateError, endDateError, onRangeChange]);

  /**
   * Clears all selected dates and validation errors.
   */
  const handleClear = useCallback(() => {
    hasUserInteracted.current = true; // Mark user interaction
    setStartDate(null);
    setEndDate(null);
    setStartDateError(null);
    setEndDateError(null);

    // Directly call onRangeChange with null values
    onRangeChange?.(null, null);
  }, [onRangeChange]);

  /**
   * Sets both date pickers to appropriate values for today/tomorrow.
   * Ensures dates are valid and after minDateTime.
   */
  const handleToday = useCallback(() => {
    hasUserInteracted.current = true; // Mark user interaction

    try {
      // Start with minDateTime for a safe 'today' value
      const defaultStart = minDateTime;

      // Verify defaultStart is valid before proceeding
      if (!defaultStart || !defaultStart.isValid) {
        console.error(
          `Failed to generate valid start date: ${defaultStart?.invalidReason || 'Unknown error'}`,
        );
        return; // Don't proceed with invalid dates
      }

      // Calculate a default end date (minimum of recommended end time or start + 1 day)
      const startPlusOneDay = defaultStart.plus({ days: 1 });
      const defaultEnd =
        recommendedEnd && recommendedEnd.isValid && recommendedEnd < startPlusOneDay
          ? recommendedEnd
          : startPlusOneDay;

      // Verify defaultEnd is valid
      if (!defaultEnd || !defaultEnd.isValid) {
        console.error(
          `Failed to generate valid end date: ${defaultEnd?.invalidReason || 'Unknown error'}`,
        );
        return; // Don't proceed with invalid dates
      }

      updateAndValidateDates(defaultStart, defaultEnd);
    } catch (error) {
      console.error('Failed to generate valid date range:', error);
    }
  }, [minDateTime, updateAndValidateDates]);

  /**
   * Determines which times should be disabled in the start date picker.
   * - Disables times in the past relative to `currentNow` for the selected day.
   * This is memoized to prevent unnecessary recalculations.
   */
  const shouldDisableStartTime = useCallback(
    (value: DateTime, view: TimeView) => {
      if (view === 'hours' || view === 'minutes') {
        const selectedDate = value.startOf('day');
        const today = currentNow.startOf('day');

        // If the selected date is today, disable times before currentNow
        if (selectedDate.equals(today)) {
          if (view === 'hours') {
            return value.hour < currentNow.hour;
          } else if (view === 'minutes') {
            // For minutes, only check if we're in the current hour
            if (value.hour === currentNow.hour) {
              return value.minute <= currentNow.minute;
            }
            return false;
          }
        }
      }
      return false;
    },
    [timezone, currentNow],
  );

  /**
   * Determines which times should be disabled in the end date picker.
   * - This wraps `shouldDisableStartTime` and additionally checks if the selected date/time
   * is before or equal to the start date/time.
   * This is memoized to prevent unnecessary recalculations.
   */
  const shouldDisableEndTime = useCallback(
    (value: DateTime, view: TimeView) => {
      // First, apply general time disabling logic (e.g., for past times of current day)
      if (shouldDisableStartTime(value, view)) {
        return true;
      }

      // Then, ensure end date/time is strictly after start date/time
      if (startDate && value <= startDate) {
        return true;
      }
      return false;
    },
    [startDate, shouldDisableStartTime],
  );

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <div className={styles.dateTimePickerWrapper}>
        <div className={styles.dateTimeContainer}>
          <div className={styles.startDateContainer}>
            <DateTimePicker
              label="Start"
              value={startDate}
              timezone={timezone}
              minDateTime={minDateTime}
              shouldDisableTime={shouldDisableStartTime}
              slotProps={{
                actionBar: {
                  actions: ['clear', 'accept'],
                },
                textField: {
                  size: 'small',
                  className: styles.datePickerField,
                  id: pickerIds?.start,
                  error: !!startDateError,
                  helperText: startDateError || undefined,
                },
              }}
              onChange={(date) => updateAndValidateDates(date, endDate)}
              onAccept={(date) => updateAndValidateDates(date, endDate)}
              format="d MMM yyyy '|' h:mm a"
              closeOnSelect={false}
            />
          </div>
          <div className={styles.endDateContainer}>
            <DateTimePicker
              label="End"
              value={endDate}
              timezone={timezone}
              minDateTime={startDate || minDateTime} // minDateTime for endDate depends on startDate
              disabled={!startDate} // Disable end date picker until start date is selected
              shouldDisableTime={shouldDisableEndTime}
              slotProps={{
                actionBar: {
                  actions: ['clear', 'accept'],
                },
                textField: {
                  helperText:
                    endDateError || (!startDate ? 'Please select start date first' : undefined),
                  size: 'small',
                  className: styles.datePickerField,
                  id: pickerIds?.end,
                  error: !!endDateError,
                },
              }}
              onChange={(date) => updateAndValidateDates(startDate, date)}
              onAccept={(date) => updateAndValidateDates(startDate, date)}
              format="d MMM yyyy '|' h:mm a"
              closeOnSelect={false}
            />
          </div>
          <div className={styles.verticalDivider}></div>
          <div className={styles.clearButtonContainer}>
            <Button
              variant="outlined"
              onClick={handleClear}
              size="small"
              className={styles.actionButton}
            >
              Clear
            </Button>
          </div>
          <div className={styles.todayButtonContainer}>
            <Button
              variant="outlined"
              onClick={handleToday}
              size="small"
              className={styles.actionButton}
            >
              Today
            </Button>
          </div>
        </div>
      </div>
    </LocalizationProvider>
  );
}
